import {
  Css,
  FilterDefs,
  GridColumn,
  GridDataRow,
  GridTableLayout,
  HeaderAction,
  PageHeaderLayout,
  RowStyles,
  Tokens,
  cardDataBlockSlot,
  cardEyebrowSlot,
  cardStatusSlot,
  cardTitleSlot,
  column,
  simpleHeader,
  toggleFilter,
  useGridTableLayoutState,
} from "@homebound/beam";
import { useCallback, useMemo } from "react";
import { FormattedDate } from "src/components";
import { StatusIndicator } from "src/components/StatusIndicator";
import { useFeatureFlag } from "src/contexts/FeatureFlags/FeatureFlagContext";
import {
  FeatureFlagType,
  Maybe,
  PlanPackageFilter,
  ReadyPlanAssetType,
  ReadyPlanStatus,
} from "src/generated/graphqlTypes";
import { useMarketFilter, useReadyPlanStatusFilter } from "src/hooks";
import {
  PlanPackagesPageDetailFragment,
  PlanPackagesQuery,
  usePlanPackagesQuery,
} from "src/routes/libraries/plan-package/list/PlanPackagesPage.generated";
import { PLAN_FALLBACK_IMG } from "src/routes/libraries/plan-package/takeoffs/utils";
import { createPlanPackageCreateUrl, createPlanPackageUrl } from "src/RouteUrls";
import { planPackageCreateRoute } from "src/routing/domains/librariesPaths";
import { readyPlanStatusToTagType } from "src/utils";
import { programDataBathRange, programDataRange } from "src/utils/programData";

export function PlanPackagesPage() {
  const isPlanPackagesV2Enabled = useFeatureFlag(FeatureFlagType.PlanPackagesV2);
  const { marketFilter: markets } = useMarketFilter();
  const { rpStatusFilter: status } = useReadyPlanStatusFilter({ omitArchived: true });
  const filterDefs: FilterDefs<PlanPackageFilterWithArchive> = useMemo(
    () => ({ markets, status, archived: toggleFilter({ label: "Show Archived" }) }),
    [markets, status],
  );
  const layoutState = useGridTableLayoutState<PlanPackageFilterWithArchive>({
    persistedFilter: { filterDefs, storageKey: "plan-package-filter" },
    search: "server",
  });
  const query = usePlanPackagesQuery({
    variables: { filter: mapToFilter(layoutState.filter, layoutState.searchString), page: defaultPlanPackagesPage },
  });
  const columns = useMemo(() => createPlanPackagesColumns(), []);
  const rowStyles = useMemo(() => createPlanPackagesRowStyles(), []);
  const createRows = useCallback(
    (data: PlanPackagesQuery | undefined) => createPlanPackagesRows(data?.planPackages.entities ?? []),
    [],
  );
  const maybeFetchNextPage = useCallback(async () => {
    if (query.data?.planPackages.pageInfo.hasNextPage) {
      await query.fetchMore({
        variables: { page: { offset: query.data.planPackages.entities.length, limit: PAGE_SIZE } },
      });
    }
  }, [query]);
  const headerActions: HeaderAction[] = useMemo(
    () => [
      {
        label: "Create",
        onClick: isPlanPackagesV2Enabled ? planPackageCreateRoute.url() : createPlanPackageCreateUrl(),
      },
    ],
    [isPlanPackagesV2Enabled],
  );

  return (
    <PageHeaderLayout pageHeader={{ title: "Plans", actions: headerActions }}>
      <GridTableLayout
        defaultView="card"
        layoutState={layoutState}
        withCardView
        tableProps={{
          query,
          createRows,
          columns,
          rowStyles,
          as: "virtual",
          cardHeight: 380,
          emptyFallback: "No plans found",
          infiniteScroll: { onEndReached: maybeFetchNextPage },
        }}
      />
    </PageHeaderLayout>
  );
}

type PlanPackageFilterWithArchive = PlanPackageFilter & { archived?: Maybe<boolean> };

export const defaultPlanPackagesFilter: PlanPackageFilter = {
  effectiveStatus: [ReadyPlanStatus.Active, ReadyPlanStatus.Draft],
};

function mapToFilter(filter: PlanPackageFilterWithArchive, searchString: string | undefined): PlanPackageFilter {
  const { archived, status, markets } = filter;
  return {
    ...(status?.nonEmpty ? { effectiveStatus: status } : archived ? {} : defaultPlanPackagesFilter),
    ...(markets?.nonEmpty ? { markets } : {}),
    ...(searchString ? { name: searchString } : {}),
  };
}

const PAGE_SIZE = 40;
export const defaultPlanPackagesPage = { offset: 0, limit: PAGE_SIZE };

type HeaderRow = { kind: "header"; data: undefined };
type PlanPackageRow = { kind: "planPackage"; data: PlanPackagesPageDetailFragment; id: string };
type PlanPackagesTableRow = HeaderRow | PlanPackageRow;

function createPlanPackagesColumns(): GridColumn<PlanPackagesTableRow>[] {
  return [
    column<PlanPackagesTableRow>({
      id: "name",
      name: "Plan Name",
      header: "Plan Name",
      mw: "240px",
      planPackage: (plan) => ({
        content: () => <div css={Css.xs.color(Tokens.TextLinkDefault).$}>{plan.name}</div>,
        value: plan.name,
        cardSlot: cardTitleSlot(plan.name),
      }),
    }),
    column<PlanPackagesTableRow>({
      id: "planCode",
      name: "Plan Code",
      header: "Plan Code",
      mw: "120px",
      planPackage: (plan) => ({
        content: plan.displayCode ?? "N/A",
        cardSlot: cardEyebrowSlot(plan.displayCode ?? "N/A"),
      }),
    }),
    column<PlanPackagesTableRow>({
      id: "sqft",
      name: "Sqft",
      header: "Sqft",
      mw: "120px",
      planPackage: ({ minProgramData, maxProgramData }) => ({
        content: programDataRange(minProgramData, maxProgramData, "sellableSqft") ?? 0,
        cardSlot: cardDataBlockSlot({
          label: "Sqft",
          value: programDataRange(minProgramData, maxProgramData, "sellableSqft") ?? 0,
        }),
      }),
    }),
    column<PlanPackagesTableRow>({
      id: "beds",
      name: "Beds",
      header: "Beds",
      mw: "80px",
      planPackage: ({ minProgramData, maxProgramData }) => ({
        content: programDataRange(minProgramData, maxProgramData, "bedrooms") ?? 0,
        cardSlot: cardDataBlockSlot({
          label: "Beds",
          value: programDataRange(minProgramData, maxProgramData, "bedrooms") ?? 0,
        }),
      }),
    }),
    column<PlanPackagesTableRow>({
      id: "baths",
      name: "Baths",
      header: "Baths",
      mw: "80px",
      planPackage: ({ minProgramData, maxProgramData }) => ({
        content: programDataBathRange(minProgramData, maxProgramData) ?? 0,
        cardSlot: cardDataBlockSlot({
          label: "Baths",
          value: programDataBathRange(minProgramData, maxProgramData) ?? 0,
        }),
      }),
    }),
    column<PlanPackagesTableRow>({
      id: "elevations",
      name: "Elevations",
      header: "Elevations",
      mw: "100px",
      planPackage: (plan) => ({
        content: elevationCount(plan),
        cardSlot: cardDataBlockSlot({ label: "Elevations", value: elevationCount(plan) }),
      }),
    }),
    column<PlanPackagesTableRow>({
      id: "options",
      name: "Options",
      header: "Options",
      mw: "90px",
      planPackage: (plan) => ({
        content: plan.activeOptionsCount,
        cardSlot: cardDataBlockSlot({ label: "Options", value: plan.activeOptionsCount }),
      }),
    }),
    column<PlanPackagesTableRow>({
      id: "status",
      name: "Status",
      header: "Status",
      mw: "100px",
      planPackage: (plan) => ({
        content: () => (
          <div css={Css.df.aic.gap1.$}>
            <StatusIndicator status={plan.status.code} type="ReadyPlan" />
            {plan.status.name}
          </div>
        ),
        value: plan.status.name,
        cardSlot: cardStatusSlot({ text: plan.status.name, type: readyPlanStatusToTagType[plan.status.code] }),
      }),
    }),
    column<PlanPackagesTableRow>({
      id: "updatedAt",
      name: "Last Updated",
      header: "Last Updated",
      mw: "120px",
      planPackage: (plan) => <FormattedDate date={plan.updatedAt} dateFormatStyle="short" />,
    }),
  ];
}

function createPlanPackagesRows(planPackages: PlanPackagesPageDetailFragment[]): GridDataRow<PlanPackagesTableRow>[] {
  return [
    simpleHeader,
    ...planPackages.map((plan) => ({
      kind: "planPackage" as const,
      data: plan,
      id: plan.id,
      imgSrc: coverPhotoUrl(plan),
    })),
  ];
}

function createPlanPackagesRowStyles(): RowStyles<PlanPackagesTableRow> {
  return { planPackage: { rowLink: ({ data }) => createPlanPackageUrl(data.id, data.aggregateLatest.id) } };
}

function elevationCount(plan: PlanPackagesPageDetailFragment): number {
  return plan.options?.filter((option) => option.type.isElevation).length ?? 0;
}

function coverPhotoUrl(plan: PlanPackagesPageDetailFragment): string {
  return (
    plan.assets?.find((asset) => asset.type.code === ReadyPlanAssetType.CoverPhoto)?.asset.previewUrl ??
    PLAN_FALLBACK_IMG
  );
}
